'use client';
import { ReactElement, useContext, useEffect, useRef, useState } from 'react';

import { IconChevronLeft, IconChevronRight } from '@veneer/core';
import { useTranslation } from 'react-i18next';

import styles from './how-your-plan-works.module.scss';
import PlanCard from './plan-card';
import { getCards } from './how-your-plan-works.utils';
import { NamespacesI18n } from '@storefront/i18n';
import { FlowOrchestratorContext } from '@storefront/app/flow-orchestrator/context';

type Card = {
  text: string;
  color: string;
  title: string;
  icon: ReactElement;
  list?: string[];
  extraContent?: string;
};

export function HowYourPlanWorks() {
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const cardsWrapperRef = useRef(null);
  const cardRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [cards, setCards] = useState<Card[]>([]);
  const { isSMB } = useContext(FlowOrchestratorContext);
  const howYourPlanWorksData = isSMB
    ? 'howYourPlanWorksSMB'
    : 'howYourPlanWorks';

  if (cards?.length === 0)
    setCards(getCards(t(howYourPlanWorksData, { returnObjects: true })));

  useEffect(() => {
    if (cardRef.current) {
      setCardWidth(cardRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const checkButtonsState = () => {
      if (cardsWrapperRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          cardsWrapperRef.current;
        setIsLeftDisabled(scrollLeft <= 0);
        setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth);
      }
    };

    checkButtonsState();
    window.addEventListener('resize', checkButtonsState);
    if (cardsWrapperRef.current) {
      cardsWrapperRef.current.addEventListener('scroll', checkButtonsState);
    }
    return () => {
      window.removeEventListener('resize', checkButtonsState);
      if (cardsWrapperRef.current) {
        cardsWrapperRef.current.removeEventListener(
          'scroll',
          checkButtonsState
        );
      }
    };
  }, [cardWidth]);

  const scrollLeft = () => {
    const newPosition = cardsWrapperRef.current.scrollLeft - cardWidth;

    const scrollLeftValue =
      newPosition < 0
        ? -cardsWrapperRef.current.scrollLeft
        : -(cardsWrapperRef.current.scrollLeft % cardWidth || cardWidth);

    cardsWrapperRef.current.scrollBy({
      left: scrollLeftValue,
      behavior: 'smooth',
    });
  };

  const scrollRight = () => {
    const newPosition = cardsWrapperRef.current.scrollLeft + cardWidth;

    const scrollRightValue =
      newPosition >
      cardsWrapperRef.current.scrollWidth - cardsWrapperRef.current.clientWidth
        ? cardsWrapperRef.current.scrollWidth -
          cardsWrapperRef.current.scrollLeft -
          cardsWrapperRef.current.clientWidth
        : cardWidth - (cardsWrapperRef.current.scrollLeft % cardWidth);

    cardsWrapperRef.current.scrollBy({
      left: scrollRightValue,
      behavior: 'smooth',
    });
  };

  return (
    <div className={styles['container']}>
      <div className={styles['container-wrapper']}>
        <div className={styles['title-wrapper']}>
          <span className={styles['title']}>
            {t('howYourPlanWorks.header')}
          </span>
          <div className={styles['chevron-wrapper']}>
            <IconChevronLeft
              className={styles[isLeftDisabled ? '' : 'chevron-left']}
              data-testid="chevron-left"
              id="chevron-left"
              color="gray12"
              onClick={scrollLeft}
              disabled={isLeftDisabled}
            />
            <IconChevronRight
              className={styles[isRightDisabled ? '' : 'chevron-right']}
              data-testid="chevron-right"
              id="chevron-right"
              color="gray12"
              onClick={scrollRight}
              disabled={isRightDisabled}
            />
          </div>
        </div>

        <div className={styles['cards-wrapper']} ref={cardsWrapperRef}>
          <div className={styles['left-filler']} />
          {cards?.map((card, index) => (
            <PlanCard
              key={card.title}
              {...card}
              ref={index === 0 ? cardRef : null}
            />
          ))}
          <div className={styles['right-filler']} />
        </div>
      </div>
    </div>
  );
}

export default HowYourPlanWorks;
